import React, { FC, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { message, Spin } from 'antd';
import { Button, PageTitle, Pagination } from '@unbooking/ui-modules';
import moment, { Moment } from 'moment';
import fileDownload from 'js-file-download';
import { useFacility } from '@common/hooks';
import { useRepository } from '@context';
import { IFeedbacksList, IFeedbackUpdateApi } from '@common/interfaces';
import { Serializer } from '@common/utils';
import DateRange from '@components/DateRange';
import { AverageRating, Tabs } from '@components';
import { CsvDocument } from '@assets/svg/icons';
import { Feedback } from './components';
import './styles.scss';

const FeedbacksPage: FC = () => {
  const { feedbackRepository } = useRepository();
  const {
    agencyName,
    facility: { agencyId, avgRating, city, country, facilityId },
  } = useFacility();
  const { t } = useTranslation();

  const tabs = useMemo(() => [`Agency passengers`, `non-${agencyName} passengers`], [agencyName]);

  const [dateStart, setDateStart] = useState<Moment | null>(moment().subtract(6, 'd'));
  const [dateEnd, setDateEnd] = useState<Moment | null>(moment());
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0]);
  const [tabDriverAgencyCount, setTabDriverAgencyCount] = useState<number>(0);
  const [tabPassengerAgencyCount, setTabPassengerAgencyCount] = useState<number>(0);

  const [pendingFeedbacks, setPendingFeedbacks] = useState<IFeedbacksList[]>([]);
  const [pendingFeedbacksPageSize, setPendingFeedbacksPageSize] = useState<number>(10);
  const [pendingFeedbacksSelectedPage, setPendingFeedbacksSelectedPage] = useState<number>(1);
  const [pendingFeedbacksTotalItems, setPendingFeedbacksTotalItems] = useState<number>(0);

  const [processedFeedbacks, setProcessedFeedbacks] = useState<IFeedbacksList[]>([]);
  const [processedFeedbacksPageSize, setProcessedFeedbacksPageSize] = useState<number>(10);
  const [processedFeedbacksSelectedPage, setProcessedFeedbacksSelectedPage] = useState<number>(1);
  const [processedFeedbacksTotalItems, setProcessedFeedbacksTotalItems] = useState<number>(0);

  const {
    data: pendingFeedbackData,
    isLoading: isPendingFeedbacksLoading,
    refetch: getPendingFeedbacks,
  } = useQuery(
    [
      'pending-feedbacks',
      dateEnd,
      dateStart,
      pendingFeedbacksPageSize,
      pendingFeedbacksSelectedPage,
    ],
    () =>
      feedbackRepository.getFeedbacks(facilityId, {
        limit: pendingFeedbacksPageSize,
        offset: (pendingFeedbacksSelectedPage - 1) * 10,
        reviewed: false,
        ...(dateStart ? { createdAfter: moment(dateStart).format('YYYY-MM-DD') } : {}),
        ...(dateEnd ? { createdBefore: moment(dateEnd).format('YYYY-MM-DD') } : {}),
        ...(tabs.indexOf(selectedTab) === 0
          ? { driverAgency: agencyId }
          : { passengerAgency: agencyId }),
      }),
    { refetchOnWindowFocus: false },
  );

  const {
    data: processedFeedbackData,
    isLoading: isProcessedFeedbacksLoading,
    refetch: getProcessedFeedbacks,
  } = useQuery(
    [
      'processed-feedbacks',
      dateEnd,
      dateStart,
      processedFeedbacksPageSize,
      processedFeedbacksSelectedPage,
    ],
    () =>
      feedbackRepository.getFeedbacks(facilityId, {
        limit: processedFeedbacksPageSize,
        offset: (processedFeedbacksSelectedPage - 1) * 10,
        reviewed: true,
        ...(dateStart ? { createdAfter: moment(dateStart).format('YYYY-MM-DD') } : {}),
        ...(dateEnd ? { createdBefore: moment(dateEnd).format('YYYY-MM-DD') } : {}),
        ...(tabs.indexOf(selectedTab) === 0
          ? { driverAgency: agencyId }
          : { passengerAgency: agencyId }),
      }),
    { refetchOnWindowFocus: false },
  );

  const { mutate: getFeedbacksExport, isLoading: fileLoading } = useMutation<
    unknown,
    AxiosError,
    { reviewed: boolean }
  >(
    'feedbacks-export',
    ({ reviewed }) =>
      feedbackRepository.exportFeedbacks(facilityId, {
        reviewed,
      }),
    {
      onSuccess: (data: any) => {
        fileDownload(data.file, data.name);
      },
      onError: (error) => {
        if (error) {
          message.error(t('feedback.messageExportError'));
        }
      },
    },
  );

  const { mutate: updateFeedbackStatus, isLoading: isFeedbackProcessing } = useMutation<
    IFeedbackUpdateApi,
    AxiosError,
    { feedbackId: string; feedbackData: IFeedbackUpdateApi }
  >(
    'update-feedback-status',
    ({ feedbackId, feedbackData }) =>
      feedbackRepository.updateFeedbackStatus(facilityId, feedbackId, feedbackData),
    {
      onSuccess: ({ status }) => {
        message.success(t('feedback.statusChanged'));
        if (status === 'not_processed') {
          if (pendingFeedbacksSelectedPage === 1) {
            getPendingFeedbacks();
          } else {
            setPendingFeedbacksSelectedPage(1);
          }
          getProcessedFeedbacks();
        } else {
          if (processedFeedbacksSelectedPage === 1) {
            getProcessedFeedbacks();
          } else {
            setProcessedFeedbacksSelectedPage(1);
          }
          getPendingFeedbacks();
        }
      },
      onError: (error) => {
        if (error.message) {
          message.error(error.message || t('common.errorMessageDefault'));
        }
      },
    },
  );

  const getColorFromRate = (rate: number): 'red' | 'yellow' | 'green' => {
    if (rate > 3) return 'green';
    if (rate === 3) return 'yellow';
    return 'red';
  };

  useEffect(() => {
    if (pendingFeedbackData) {
      setPendingFeedbacks(pendingFeedbackData.results.map(Serializer.formatFeedback));
      setPendingFeedbacksTotalItems(pendingFeedbackData.count);
    }

    if (processedFeedbackData) {
      setProcessedFeedbacks(processedFeedbackData.results.map(Serializer.formatFeedback));
      setProcessedFeedbacksTotalItems(processedFeedbackData.count);
    }
  }, [pendingFeedbackData, processedFeedbackData]);

  useEffect(() => {
    if (pendingFeedbacksTotalItems || processedFeedbacksTotalItems) {
      const totalItems = pendingFeedbacksTotalItems + processedFeedbacksTotalItems;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      tabs.indexOf(selectedTab) === 0
        ? setTabDriverAgencyCount(totalItems)
        : setTabPassengerAgencyCount(totalItems);
    }
  }, [pendingFeedbacksTotalItems, processedFeedbacksTotalItems, tabs, selectedTab]);

  const TabContent: FC = () =>
    useMemo(
      () => (
        <>
          <div className="feedback">
            <div className="feedback-list">
              <div className="feedback-list-header">
                <div className="feedback-list-title">{t('feedback.pending')}</div>
                {pendingFeedbacks.length > 0 ? (
                  <div className="feedback-list-export">
                    <Button
                      className="btn-export"
                      disabled={fileLoading}
                      icon={<CsvDocument />}
                      text={t('feedback.btnExportPending')}
                      variant="primary"
                      onClick={() => getFeedbacksExport({ reviewed: false })}
                    />
                  </div>
                ) : null}
              </div>

              {pendingFeedbacks.length > 0 ? (
                pendingFeedbacks.map((item: IFeedbacksList) => (
                  <Feedback
                    color="grey"
                    isProcessing={isFeedbackProcessing}
                    item={item}
                    key={item.id}
                    showRating
                    updateFeedbackStatus={updateFeedbackStatus}
                  />
                ))
              ) : (
                <div className="feedback-list-empty">{t('common.emptyList')} ...</div>
              )}
            </div>

            <div className="row">
              {pendingFeedbacksTotalItems > 10 && (
                <Pagination
                  className="pagination"
                  selectedPage={pendingFeedbacksSelectedPage}
                  showJumper
                  showPageSize
                  totalPages={pendingFeedbacksTotalItems}
                  variant="dark"
                  onPageChange={setPendingFeedbacksSelectedPage}
                  onPageSizeChange={setPendingFeedbacksPageSize}
                />
              )}
            </div>
          </div>
          <div className="feedback">
            <div className="feedback-list">
              <div className="feedback-list-header">
                <div className="feedback-list-title">{t('feedback.reviewed')}</div>
                {processedFeedbacks.length > 0 ? (
                  <div className="feedback-list-export">
                    <Button
                      className="btn-export"
                      disabled={fileLoading}
                      icon={<CsvDocument />}
                      text={t('feedback.btnExportReviewed')}
                      variant="primary"
                      onClick={() => getFeedbacksExport({ reviewed: true })}
                    />
                  </div>
                ) : null}
              </div>

              {processedFeedbacks.length > 0 ? (
                processedFeedbacks.map((item: IFeedbacksList) => (
                  <Feedback
                    color={getColorFromRate(item.feedback.rate)}
                    isProcessing={isFeedbackProcessing}
                    item={item}
                    key={item.id}
                    showRating
                    updateFeedbackStatus={updateFeedbackStatus}
                  />
                ))
              ) : (
                <div className="feedback-list-empty">{t('common.emptyList')} ...</div>
              )}
            </div>

            <div className="row">
              {processedFeedbacksTotalItems > 10 && (
                <Pagination
                  className="pagination"
                  selectedPage={processedFeedbacksSelectedPage}
                  showJumper
                  showPageSize
                  totalPages={processedFeedbacksTotalItems}
                  variant="dark"
                  onPageChange={setProcessedFeedbacksSelectedPage}
                  onPageSizeChange={setProcessedFeedbacksPageSize}
                />
              )}
            </div>
          </div>
        </>
      ),
      [],
    );

  return (
    <section className="hbh-container feedbacks">
      <PageTitle
        bottomLine
        title={
          city && country ? `${t('feedback.pageTitle')} ${city}, ${country} | ${agencyName}` : ''
        }
        filters={
          <>
            <DateRange
              dateStart={dateStart}
              dateEnd={dateEnd}
              displayFormat="DD MMM YYYY"
              endDatePlaceholderText="To"
              label={`${t('feedback.timePeriod')}:`}
              startDatePlaceholderText="From"
              variant="dark"
              onDateChange={({ startDate, endDate }) => {
                setDateStart(startDate);
                setDateEnd(endDate);
              }}
            />
            <AverageRating
              backgroundColor={getColorFromRate(avgRating ?? 0)}
              facilityName={country}
              type="brief"
              value={avgRating ? parseFloat(avgRating?.toFixed(1)) : undefined}
            />
          </>
        }
      />
      <section className="feedbacks-content">
        <Tabs
          badges={[tabDriverAgencyCount, tabPassengerAgencyCount]}
          className="feedbacks-tabs"
          selectedTab={tabs.indexOf(selectedTab)}
          tabs={tabs}
          onTabSelect={setSelectedTab}
        />

        <Spin size="large" spinning={isPendingFeedbacksLoading || isProcessedFeedbacksLoading}>
          {selectedTab === tabs[0] ? <TabContent /> : null}
          {selectedTab === tabs[1] ? <TabContent /> : null}
        </Spin>
      </section>
    </section>
  );
};

export default FeedbacksPage;
